import React, { type ReactNode, useCallback, useMemo, useState } from 'react'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../common/integrations'
import { IntegrationConnection } from '../../../Integration/Connectors/IntegrationConnection'
import { OnboardingStepHeader } from '../OnboardingStepHeader/OnboardingStepHeader'
import { OnboardingIntegrationSelector } from '../OnboardingIntegrationSelector/OnboardingIntegrationSelector'
import { Button } from '../../../../components/Button/Button'
import { Capability } from '../../../../models/Onboarding'

const resolvePostConnectPath = (
  postConnectRedirectPath: string | ((serviceId: IntegrationServiceId) => string) | undefined,
  serviceId: IntegrationServiceId,
): string | undefined => (typeof postConnectRedirectPath === 'function' ? postConnectRedirectPath(serviceId) : postConnectRedirectPath)

export interface OnboardingIntegrationStepProps {
  icon: ReactNode
  headerTitle: string
  headerSubtitle: string
  serviceIds: IntegrationServiceId[]
  selectorDisplayMode?: 'carousel' | 'grid'
  secondaryServiceIds?: IntegrationServiceId[]
  secondaryServiceIdsLabel?: string
  notificationText?: ReactNode
  connectedWithoutSync?: IntegrationServiceId[]
  postConnectRedirectPath?: string | ((serviceId: IntegrationServiceId) => string)
  secondaryAction?: ReactNode
  capability?: Capability
  onIntegrationSelect?: (serviceId: IntegrationServiceId) => void
}

export const OnboardingIntegrationStep: React.FC<OnboardingIntegrationStepProps> = ({
  icon,
  headerTitle,
  headerSubtitle,
  serviceIds,
  selectorDisplayMode,
  secondaryServiceIds,
  secondaryServiceIdsLabel,
  notificationText,
  connectedWithoutSync = [],
  postConnectRedirectPath,
  secondaryAction,
  capability,
  onIntegrationSelect,
}) => {
  const [activeConnectId, setActiveConnectId] = useState<IntegrationServiceId | null>(null)

  const connectedWithoutSyncSet = useMemo(() => new Set(connectedWithoutSync), [connectedWithoutSync])

  const handleSelect = useCallback(
    (serviceId: IntegrationServiceId) => {
      onIntegrationSelect?.(serviceId)
      if (connectedWithoutSyncSet.has(serviceId)) {
        const postConnectPath = resolvePostConnectPath(postConnectRedirectPath, serviceId)
        if (postConnectPath) {
          window.location.href = postConnectPath
        }
      } else {
        setActiveConnectId(serviceId)
      }
    },
    [connectedWithoutSyncSet, onIntegrationSelect, postConnectRedirectPath],
  )

  const connectableServiceIds = useMemo(() => serviceIds.filter((serviceId) => !connectedWithoutSyncSet.has(serviceId)), [serviceIds, connectedWithoutSyncSet])

  const connectableSecondaryServiceIds = useMemo(
    () => (secondaryServiceIds ?? []).filter((serviceId) => !connectedWithoutSyncSet.has(serviceId)),
    [secondaryServiceIds, connectedWithoutSyncSet],
  )

  const allConnectableIds = [...connectableServiceIds, ...connectableSecondaryServiceIds]

  const canShowPrimary = connectableServiceIds.length > 0
  const shouldShowWithoutSync = connectedWithoutSync.length > 0

  const canShowSecondary = connectableSecondaryServiceIds.length > 0 && !shouldShowWithoutSync

  return (
    <div className='onboarding-integration-step d-flex flex-column align-items-center gap-4 w-100'>
      <OnboardingStepHeader icon={icon} title={headerTitle} subtitle={headerSubtitle} />
      {shouldShowWithoutSync ? (
        <div className='d-flex flex-column align-items-center gap-4 w-100'>
          <span className='fw-semibold'>{getIntegrationDisplayName(connectedWithoutSync[0])} is already connected to your Account</span>
          <Button variant='primary' onClick={() => handleSelect(connectedWithoutSync[0])}>
            Sync {capability === Capability.CONTACT_SOURCE ? 'contacts' : 'meetings'} from {getIntegrationDisplayName(connectedWithoutSync[0])}
          </Button>
          {canShowPrimary ? (
            <>
              <span className='fw-semibold'>OR</span>
              <span className='fw-semibold'>Choose another {capability === Capability.CONTACT_SOURCE ? 'CRM' : 'Calendar'} source</span>
            </>
          ) : null}
        </div>
      ) : null}
      {canShowPrimary ? (
        <OnboardingIntegrationSelector
          serviceIds={connectableServiceIds}
          onSelect={handleSelect}
          pageSize={selectorDisplayMode === 'carousel' ? 8 : undefined}
          showMoreThreshold={selectorDisplayMode === 'grid' ? 6 : undefined}
        />
      ) : null}
      {canShowSecondary ? (
        <div className='d-flex flex-column align-items-center gap-3 w-100'>
          {secondaryServiceIdsLabel && canShowPrimary ? <p className='m-0 fw-bold text-center text-dark'>{secondaryServiceIdsLabel}</p> : null}
          <OnboardingIntegrationSelector serviceIds={connectableSecondaryServiceIds} onSelect={handleSelect} />
        </div>
      ) : null}
      {notificationText ? (
        <div className='d-flex flex-column align-items-center gap-2 text-secondary text-center px-3'>
          <i className='bi bi-info-circle flex-shrink-0' />
          <small>{notificationText}</small>
        </div>
      ) : null}
      {secondaryAction ? <div className='d-flex flex-column align-items-center'>{secondaryAction}</div> : null}
      {allConnectableIds.map((serviceId) => (
        <IntegrationConnection
          key={`connection-${serviceId}`}
          serviceId={serviceId}
          startConnect={activeConnectId === serviceId}
          onConnected={() => setActiveConnectId(null)}
          postConnectRedirectPath={resolvePostConnectPath(postConnectRedirectPath, serviceId)}
        />
      ))}
    </div>
  )
}
